/**
 * Client-side image compression utilities
 * Handles resizing and WebP conversion for optimal upload performance
 */

import { once } from '@markku/utils'

const MAX_WIDTH = 1280
const COMPRESSION_QUALITY = 0.8

const supportsWebP = once((): boolean => {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  return canvas.toDataURL('image/webp').startsWith('data:image/webp')
})

const getOutputFormat = () => {
  const useWebP = supportsWebP()
  return {
    mimeType: useWebP ? 'image/webp' : 'image/jpeg',
    extension: useWebP ? '.webp' : '.jpg',
  }
}

const compressBitmap = async (
  bitmap: ImageBitmap,
  originalFile: File,
): Promise<File | null> => {
  // Calculate dimensions (maintain aspect ratio)
  const scale = Math.min(1, MAX_WIDTH / bitmap.width)
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  // Create canvas and draw
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) return null

  ctx.drawImage(bitmap, 0, 0, width, height)

  // Convert to blob
  const format = getOutputFormat()
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, format.mimeType, COMPRESSION_QUALITY)
  })

  if (!blob) return null

  // Create new file with appropriate extension
  const newFilename = originalFile.name.replace(/\.\w+$/, format.extension)
  return new File([blob], newFilename, {
    type: format.mimeType,
    lastModified: originalFile.lastModified,
  })
}

/**
 * Compress an image file for optimal upload performance
 * - Resizes to max 1280px width (maintains aspect ratio)
 * - Converts to WebP format at 80% quality (fallback to JPG at 80% if WebP not supported)
 * - Handles EXIF orientation automatically
 * - Falls back to original only if compression completely fails
 *
 * @param file - Image file to compress
 * @returns Compressed image file (WebP or JPG) or original if compression fails
 */
export async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file
  }

  // Try with EXIF support (iOS 17+, Chrome 94+)
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image',
    })
    const compressed = await compressBitmap(bitmap, file)
    if (compressed) return compressed
  } catch {
    // EXIF support failed, will try fallback
  }

  // Fallback without EXIF support (iOS 14-16)
  try {
    const bitmap = await createImageBitmap(file)
    const compressed = await compressBitmap(bitmap, file)
    if (compressed) {
      console.log(
        'Compressed without EXIF support (may be rotated incorrectly)',
      )
      return compressed
    }
  } catch {
    // Compression failed completely
  }

  // All compression failed, return original
  console.warn('All compression attempts failed, uploading original')
  return file
}
